import { Box, Typography } from "@mui/material";
import { FormikProps } from "formik";
import { Trans, useTranslation } from "react-i18next";
import i18n from "../../../../../i18n/i18n";
import { Link } from "react-router-dom";
import { FreelancerOnboardingMyPresentationStepViewModel } from "../../../../../ViewModels/FreelancerOnboardingMyPresentationStepViewModel";
import HtmlEditor, { getExtensions } from "../../../../UI/HtmlEditor";
import TranslatableFieldWrapper from "../../../../translation/TranslatableFieldWrapper";
import { TranslatableFieldUiState } from "../../../../../hooks/useTranslatableForm";
import { LanguageCode } from "../../../../../models/enums/LanguageCode";

interface FreelancerOnboardingPresentationSectionProps {
	readonly formik: FormikProps<FreelancerOnboardingMyPresentationStepViewModel>;
	readonly editingLanguage: LanguageCode;
	readonly onLanguageChange: (lang: LanguageCode) => void;
	readonly presentationValue: string;
	readonly onPresentationChange: (value: string) => void;
	readonly isStale?: boolean;
	/** Forwarded to TranslatableFieldWrapper (see useTranslatableForm.getFieldUiState). */
	readonly uiState?: TranslatableFieldUiState;
	readonly sourceLanguage?: LanguageCode;
	readonly showLanguageSelector?: boolean;
	readonly showAiFirstNote?: boolean;
	readonly isDraft?: boolean;
	readonly onBeginDraft?: () => void;
	readonly onCancelDraft?: () => void;
	readonly canHandBackToAi?: boolean;
	readonly isRegenerating?: boolean;
	readonly onEnableAi?: () => void;
	/**
	 * Locks the editor through Tiptap's NATIVE editable={false} (useTranslatableForm.
	 * isFieldReadOnly): the AI-managed translation must not be editable, but the wrapper's old
	 * pointer-events:none tarp also killed the editor's internal scroll — a long presentation was
	 * unreadable past the first screen — plus text selection/copy and assistive-tech access.
	 */
	readonly readOnly?: boolean;
	/**
	 * Forwarded to TranslatableFieldWrapper: the parent owns the useTranslatableForm and passes its
	 * translationsLoaded here so the Tiptap editor remounts once when the EN values land (it only
	 * reads `content` on create — see the wrapper's contentReadyToken doc).
	 */
	readonly contentReadyToken?: React.Key | boolean;
}

export default function FreelancerOnboardingMyPresentationPresentationBloc({
	formik,
	editingLanguage,
	onLanguageChange,
	presentationValue,
	onPresentationChange,
	isStale,
	uiState,
	sourceLanguage,
	showLanguageSelector,
	showAiFirstNote,
	isDraft,
	onBeginDraft,
	onCancelDraft,
	canHandBackToAi,
	isRegenerating,
	onEnableAi,
	contentReadyToken,
	readOnly,
}: FreelancerOnboardingPresentationSectionProps) {
	const { t } = useTranslation();
	const tLang = i18n.getFixedT(editingLanguage.toLowerCase());

	return (
		<Box sx={{ marginBottom: 4, zIndex: 0 }}>
			<TranslatableFieldWrapper
				label={t("freelancerOnboardingMyPresentation.presentation.title")}
				editingLanguage={editingLanguage}
				onLanguageChange={onLanguageChange}
				isStale={isStale}
				uiState={uiState}
				sourceLanguage={sourceLanguage}
				showLanguageSelector={showLanguageSelector}
				showAiFirstNote={showAiFirstNote}
				isDraft={isDraft}
				onBeginDraft={onBeginDraft}
				onCancelDraft={onCancelDraft}
				canHandBackToAi={canHandBackToAi}
				isRegenerating={isRegenerating}
				onEnableAi={onEnableAi}
				contentReadyToken={contentReadyToken}
			>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
					<Trans
						i18nKey={"freelancerOnboardingMyPresentation.presentation.description"}
						components={{
							a: <Link to="" />
						}}
					/>
				</Typography>
				<HtmlEditor
					key={editingLanguage}
					// Native lock: mui-tiptap applies `editable` changes live (editor.setEditable) and
					// disables the toolbar, so scroll/selection/copy keep working on locked content.
					editable={!readOnly}
					emitUpdatesOnlyWhenFocused
					sx={{
						"& .MuiTiptap-FieldContainer-root .MuiTiptap-RichTextContent-root .ProseMirror":
							{
								height: "400px !important"
							}
					}}
					extensions={getExtensions(
						tLang("freelancerOnboardingMyPresentation.presentation.placeholder")
					)}
					content={presentationValue}
					onUpdate={({ editor }) => {
						const newValue = editor.getHTML();
						onPresentationChange(newValue);
					}}
					onBlur={() => {
						formik.setFieldTouched("presentation", true);
					}}
					placeholder={tLang("freelancerOnboardingMyPresentation.presentation.placeholder")}
				/>
			</TranslatableFieldWrapper>
			{formik.touched.presentation && formik.errors.presentation && (
				<Typography color="error" variant="body1" sx={{ mt: 1, display: "block" }}>
					{formik.errors.presentation}
				</Typography>
			)}
		</Box>
	);
}
